import React from 'react';
import {Body, Headline} from '@lightricks/react-design-system';
import type {OffPlatformWeightedSlice} from '@/api/fetchers/crm/offPlatformCreators';
import styles from './OffPlatformProfile.module.scss';

function titleCase(value?: string | null) {
  if (!value) {
    return '';
  }
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function sliceLabel(item: OffPlatformWeightedSlice) {
  return item.name || titleCase(item.code) || 'Unknown';
}

function AudienceGroup({
  title,
  items,
}: {
  title: string;
  items?: OffPlatformWeightedSlice[];
}) {
  const ranked = [...(items ?? [])]
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 6);
  if (ranked.length === 0) {
    return null;
  }

  return (
    <div className={styles.audienceGroup}>
      <Headline size="sm">{title}</Headline>
      {ranked.map((item) => {
        const percent = Math.round(item.weight * 100);
        return (
          <div
            className={styles.audienceRow}
            key={`${item.code}-${item.name}-${item.weight}`}
          >
            <div className={styles.audienceLabelRow}>
              <Body size="sm">{sliceLabel(item)}</Body>
              <Body size="sm">{percent}%</Body>
            </div>
            <div className={styles.audienceTrack}>
              <div
                className={styles.audienceFill}
                style={{width: `${Math.min(percent, 100)}%`}}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function OffPlatformAudience({
  genders,
  ages,
  geoCountries,
  languages,
}: {
  genders?: OffPlatformWeightedSlice[];
  ages?: OffPlatformWeightedSlice[];
  geoCountries?: OffPlatformWeightedSlice[];
  languages?: OffPlatformWeightedSlice[];
}) {
  const empty =
    !genders?.length &&
    !ages?.length &&
    !geoCountries?.length &&
    !languages?.length;

  if (empty) {
    return <Body size="md">No audience snapshot stored for this creator.</Body>;
  }

  return (
    <div className={styles.audienceGrid}>
      <AudienceGroup title="Gender" items={genders} />
      <AudienceGroup title="Age" items={ages} />
      <AudienceGroup title="Countries" items={geoCountries} />
      <AudienceGroup title="Languages" items={languages} />
    </div>
  );
}
